import { Editor, EditorQueries, Frame } from '@candulabs/craft';
import { css, styled } from 'canoe';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { createGlobalStyle } from 'styled-components';

import { useAuthentication } from 'commons/hooks';
import { useIncrement } from 'commons/hooks/useIncrement';
import { EditorControls } from 'craft/components/Canvas';
import { ContextMenu } from 'craft/components/ContextMenu';
import { CssSelectorStylesheets } from 'craft/components/CssSelectorStylesheets';
import { EventManager } from 'craft/components/EventManager';
import { Indicator } from 'craft/components/Indicator';
import { SelectedToolbar } from 'craft/components/SelectedToolbar';
import ValidationErrorHandler from 'craft/components/ValidationErrorHandler';
import { Palette } from 'craft/containers/Palette';
import { Viewport, VIEWPORT_WRAPPER_ID } from 'craft/containers/Viewport';
import { resolvers } from 'craft/resolvers';
import { CLASS_NAME as IMAGE_CLASS_NAME } from 'craft/resolvers/Image';
import {
  setViewportWrapperDom,
  useCurrentSnapshot,
  useLoadEditorStyleguide,
  useViewportBreakpoint,
} from 'craft/state';
import { CraftNodes } from 'craft/types';
import RebootReset from 'styleguide/reboot-reverse.css';

import { CraftMode } from './Craft';
import { EventHandlers } from './EventHandlers';
import { ModeToggler } from './ModeToggler';
import { RenderNode } from './RenderNode';
import { normalizeNodes } from './normalizeNodes';

const EditorContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: row;
  overflow: auto;
`;

const ViewportWrapper = styled.div`
  ${({ theme }) => css`
    flex-grow: 1;
    overflow: auto;
    position: relative;
    z-index: 0;
    background-color: ${theme.colors.OnyxGray};
  `}
`;

export const GlobalEditorWrapperStyles = createGlobalStyle`
  .${IMAGE_CLASS_NAME} {
    line-height: 1;
  }
`;

// Craft understands only two modes but EditorWrapper supports three
interface EditorWrapperProps {
  contentId: string;
  documentId: number;
  onStateChange: (a: any) => void;
  mode: CraftMode;
  nodes?: CraftNodes;
  actionBar?: React.ReactNode;
}

export const EditorWrapper = React.memo(
  ({ contentId, documentId, onStateChange, mode, nodes, actionBar }: EditorWrapperProps) => {
    const dispatch = useDispatch();
    const reduxStore = useStore();
    const scrollableRef = useRef<HTMLElement | null>(null);
    const viewportBreakpoint = useViewportBreakpoint();
    const { snapshot } = useCurrentSnapshot();
    const { value: editIterator, increment } = useIncrement(1);

    useLoadEditorStyleguide();
    useEffect(() => {
      // reset left horizontal scroll position of canvas every time you change breakpoint
      // to avoid weirdness with scrolling
      scrollableRef.current?.scrollTo({ left: 0 });
    }, [viewportBreakpoint]);

    useEffect(() => {
      // save viewport wrapper ref to the store
      dispatch(setViewportWrapperDom(scrollableRef));
    }, []);

    const craftMode = mode === 'locked' ? 'edit' : mode;

    return (
      <>
        <GlobalEditorWrapperStyles />
        <RebootReset />
        <Editor
          mode={craftMode}
          enabled={false}
          Indicator={Indicator}
          resolver={resolvers}
          onNodesChange={(query: EditorQueries) => {
            setTimeout(() => {
              // slight delay needed to give the dom a chance to update with the node changes
              // before the iterator is incremented (snapshot placeholder was getting old dom without this)
              increment();
            }, 20);
            return onStateChange(query.getSerializedNodes());
          }}
          normalizeNodes={normalizeNodes}
          onRender={RenderNode}
          handlers={(store) => new EventHandlers(store, reduxStore)}
          key={`${documentId}-${snapshot?.id || 'no-snapshot'}`}
          nodes={nodes}
        >
          <>
            <CssSelectorStylesheets />
            <ModeToggler mode={craftMode} />
            {mode === 'edit' && (
              <>
                <ValidationErrorHandler />
                <EventManager />
                <ContextMenu />
                <SelectedToolbar />
              </>
            )}

            <EditorContainer id="editor-container">
              <Palette />

              <EditorControls contentId={contentId} actionBar={actionBar} mode={mode}>
                <ViewportWrapper
                  ref={(dom: any) => {
                    scrollableRef.current = dom;
                  }}
                  id={VIEWPORT_WRAPPER_ID}
                >
                  <Viewport contentId={contentId} editIterator={editIterator} mode={mode}>
                    <Frame />
                  </Viewport>
                </ViewportWrapper>
              </EditorControls>
            </EditorContainer>
          </>
        </Editor>
      </>
    );
  },
);

EditorWrapper.displayName = 'EditorWrapper';
